import { cn } from '@/lib/utils';
import { usePlatformStore } from '@/store/platformStore';
import type { LogEntry, LogLevel, LogSource } from '@/types/platform';
import {
    AlertCircle,
    AlertTriangle,
    Bug,
    ChevronDown,
    Cpu,
    Filter,
    Hammer,
    Info,
    MonitorCog,
    Pause,
    Play,
    Rocket,
    ScrollText,
    Server,
    Skull,
    Sparkles,
    Trash2,
    Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/* ================================================================== */
/*  Logs Viewer Panel                                                 */
/*                                                                    */
/*  Real-time log stream for workspace, build, deploy, runtime, RPC,  */
/*  AI, and system events. Native observability — no external tools.  */
/* ================================================================== */

/* -- Level config -------------------------------------------------- */

const LEVEL_CONFIG: Record<LogLevel, { icon: typeof Info; color: string; bg: string }> = {
  debug: { icon: Bug, color: 'text-[#666]', bg: 'bg-[#666]/10' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  warn: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  error: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
  fatal: { icon: Skull, color: 'text-red-500', bg: 'bg-red-500/20' },
};

const SOURCE_CONFIG: Record<LogSource, { icon: typeof Server; label: string; color: string }> = {
  workspace: { icon: Server, label: 'Workspace', color: 'text-cyan-400' },
  build: { icon: Hammer, label: 'Build', color: 'text-orange-400' },
  deploy: { icon: Rocket, label: 'Deploy', color: 'text-green-400' },
  runtime: { icon: Cpu, label: 'Runtime', color: 'text-purple-400' },
  rpc: { icon: Zap, label: 'RPC', color: 'text-yellow-400' },
  ai: { icon: Sparkles, label: 'AI', color: 'text-pink-400' },
  system: { icon: MonitorCog, label: 'System', color: 'text-gray-400' },
};

/* -- Demo logs generator ------------------------------------------- */

function generateDemoLogs(): LogEntry[] {
  const now = Date.now();
  const sources: LogSource[] = ['workspace', 'build', 'deploy', 'runtime', 'rpc', 'ai', 'system'];
  const levels: LogLevel[] = ['debug', 'info', 'info', 'info', 'warn', 'error'];
  const messages: Record<LogSource, string[]> = {
    workspace: [
      'Container started successfully',
      'File system mounted',
      'Port 3000 bound',
      'Health check passed',
    ],
    build: [
      'Compiling contracts...',
      'solc 0.8.24 loaded',
      'Compilation successful (342ms)',
      'Warning: unused variable "x"',
      'Artifacts written to /out',
    ],
    deploy: [
      'Deploying to Sepolia...',
      'Transaction sent: 0x7a3f...',
      'Contract deployed at 0x1234...abcd',
      'Gas used: 342,891',
      'Deployment confirmed (block #19234567)',
    ],
    runtime: [
      'Contract call: transfer()',
      'Event emitted: Transfer(from, to, amount)',
      'Gas estimation: 52,341',
    ],
    rpc: [
      'eth_call → 200 (12ms)',
      'eth_getBlockByNumber → 200 (8ms)',
      'eth_sendRawTransaction → 200 (45ms)',
      'Rate limit: 847/10,000 requests used',
    ],
    ai: [
      'Code generation started',
      'Model: gpt-4o (Azure)',
      'Tokens used: 2,341',
      'Security audit: 3 findings',
      'Gas optimization: 12% savings identified',
    ],
    system: [
      'Platform version 2.1.0',
      'SSL certificate valid (89 days)',
      'Backup completed',
      'Memory usage: 67%',
    ],
  };

  return Array.from({ length: 50 }, (_, i) => {
    const source = sources[i % sources.length];
    const msgs = messages[source];
    return {
      id: `log-${i}`,
      workspaceId: 'ws-demo',
      source,
      level: levels[Math.floor(Math.random() * levels.length)],
      message: msgs[i % msgs.length],
      timestamp: new Date(now - (50 - i) * 3000).toISOString(),
    };
  });
}

/* -- Filter dropdown ----------------------------------------------- */

function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
  isOpen,
  onOpenChange,
}: {
  label: string;
  options: { value: string; label: string; color?: string }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => onOpenChange(!isOpen)}
        className={cn(
          'flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border transition-colors',
          selected.size < options.length
            ? 'border-[#007acc] text-[#007acc] bg-[#007acc]/10'
            : 'border-[#3e3e3e] text-[#888] hover:text-white',
        )}
      >
        <Filter className="w-2.5 h-2.5" />
        {label}
        {selected.size < options.length && (
          <span className="bg-[#007acc] text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[8px]">
            {selected.size}
          </span>
        )}
        <ChevronDown className="w-2.5 h-2.5" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-[#252526] border border-[#454545] rounded shadow-xl z-50 py-1 min-w-[140px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onToggle(opt.value)}
              className="w-full flex items-center gap-2 px-2.5 py-1 text-[11px] text-left hover:bg-[#2a2d2e]"
            >
              <div
                className={cn(
                  'w-3 h-3 rounded-sm border flex items-center justify-center text-[8px]',
                  selected.has(opt.value)
                    ? 'bg-[#007acc] border-[#007acc] text-white'
                    : 'border-[#555]',
                )}
              >
                {selected.has(opt.value) && '✓'}
              </div>
              <span className={opt.color ?? 'text-[#ccc]'}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -- Log Line ------------------------------------------------------ */

function LogLine({ entry }: { entry: LogEntry }) {
  const levelConf = LEVEL_CONFIG[entry.level];
  const sourceConf = SOURCE_CONFIG[entry.source];
  const LevelIcon = levelConf.icon;
  const SourceIcon = sourceConf.icon;
  const time = new Date(entry.timestamp);
  const timeStr = time.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <div
      className={cn(
        'flex items-start gap-1.5 px-3 py-0.5 font-mono text-[11px] hover:bg-[#2a2d2e]/50 group',
        entry.level === 'error' && 'bg-red-500/5',
        entry.level === 'fatal' && 'bg-red-500/10',
      )}
    >
      {/* Timestamp */}
      <span className="text-[#555] shrink-0 w-[60px]">{timeStr}</span>

      {/* Level badge */}
      <span className={cn('shrink-0 w-4', levelConf.color)} title={entry.level}>
        <LevelIcon className="w-3 h-3" />
      </span>

      {/* Source badge */}
      <span
        className={cn('shrink-0 flex items-center gap-0.5', sourceConf.color)}
        title={sourceConf.label}
      >
        <SourceIcon className="w-3 h-3" />
        <span className="text-[9px] w-[52px] truncate">{sourceConf.label}</span>
      </span>

      {/* Message */}
      <span className="text-[#d4d4d4] flex-1 break-all">{entry.message}</span>
    </div>
  );
}

/* ================================================================== */
/*  Main Component                                                    */
/* ================================================================== */

export default function LogsViewerPanel() {
  const platformLogs = usePlatformStore((s) => s.logs);
  const isStreaming = usePlatformStore((s) => s.isStreamingLogs);
  const clearLogs = usePlatformStore((s) => s.clearLogs);

  // Filters
  const allLevels = new Set<LogLevel>(['debug', 'info', 'warn', 'error', 'fatal']);
  const allSources = new Set<LogSource>([
    'workspace',
    'build',
    'deploy',
    'runtime',
    'rpc',
    'ai',
    'system',
  ]);
  const [levelFilter, setLevelFilter] = useState<Set<string>>(new Set(allLevels));
  const [sourceFilter, setSourceFilter] = useState<Set<string>>(new Set(allSources));
  const [searchQuery, setSearchQuery] = useState('');
  const [showLevelFilter, setShowLevelFilter] = useState(false);
  const [showSourceFilter, setShowSourceFilter] = useState(false);
  const [paused, setPaused] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Use demo logs when platform logs empty
  const [demoLogs] = useState(() => generateDemoLogs());
  const rawLogs = platformLogs.length > 0 ? platformLogs : demoLogs;

  // Filtered logs
  const filteredLogs = rawLogs.filter((log) => {
    if (!levelFilter.has(log.level)) return false;
    if (!sourceFilter.has(log.source)) return false;
    if (searchQuery && !log.message.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Auto-scroll
  useEffect(() => {
    if (!paused && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filteredLogs.length, paused]);

  const toggleFilter = useCallback(
    (set: Set<string>, value: string, setter: (s: Set<string>) => void) => {
      const next = new Set(set);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      setter(next);
    },
    [],
  );

  const levelOptions = [
    { value: 'debug', label: 'Debug', color: 'text-[#666]' },
    { value: 'info', label: 'Info', color: 'text-blue-400' },
    { value: 'warn', label: 'Warning', color: 'text-yellow-400' },
    { value: 'error', label: 'Error', color: 'text-red-400' },
    { value: 'fatal', label: 'Fatal', color: 'text-red-500' },
  ];

  const sourceOptions = Object.entries(SOURCE_CONFIG).map(([value, cfg]) => ({
    value,
    label: cfg.label,
    color: cfg.color,
  }));

  const errorCount = filteredLogs.filter((l) => l.level === 'error' || l.level === 'fatal').length;
  const warnCount = filteredLogs.filter((l) => l.level === 'warn').length;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[#2d2d2d] shrink-0">
        {/* Search */}
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter logs..."
          className="flex-1 bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-0.5 text-[11px] text-white placeholder-[#555] focus:border-[#007acc] outline-none min-w-0"
        />

        {/* Level filter */}
        <FilterDropdown
          label="Level"
          options={levelOptions}
          selected={levelFilter}
          onToggle={(v) => toggleFilter(levelFilter, v, setLevelFilter)}
          isOpen={showLevelFilter}
          onOpenChange={setShowLevelFilter}
        />

        {/* Source filter */}
        <FilterDropdown
          label="Source"
          options={sourceOptions}
          selected={sourceFilter}
          onToggle={(v) => toggleFilter(sourceFilter, v, setSourceFilter)}
          isOpen={showSourceFilter}
          onOpenChange={setShowSourceFilter}
        />

        {/* Pause / Resume */}
        <button
          onClick={() => setPaused(!paused)}
          className={cn(
            'p-1 rounded transition-colors',
            paused
              ? 'text-yellow-400 hover:bg-yellow-400/10'
              : 'text-[#888] hover:text-white hover:bg-[#3e3e3e]',
          )}
          title={paused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
        >
          {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
        </button>

        {/* Clear */}
        <button
          onClick={clearLogs}
          className="p-1 text-[#888] hover:text-white rounded hover:bg-[#3e3e3e] transition-colors"
          title="Clear logs"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-3 px-3 py-1 text-[10px] text-[#666] border-b border-[#2d2d2d] shrink-0">
        <span>{filteredLogs.length} entries</span>
        {errorCount > 0 && (
          <span className="text-red-400 flex items-center gap-0.5">
            <AlertCircle className="w-2.5 h-2.5" />
            {errorCount} errors
          </span>
        )}
        {warnCount > 0 && (
          <span className="text-yellow-400 flex items-center gap-0.5">
            <AlertTriangle className="w-2.5 h-2.5" />
            {warnCount} warnings
          </span>
        )}
        {isStreaming && (
          <span className="text-green-400 flex items-center gap-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Streaming
          </span>
        )}
        {paused && <span className="text-yellow-400">Paused</span>}
      </div>

      {/* Log lines */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        {filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#666] gap-2">
            <ScrollText className="w-8 h-8" />
            <p className="text-[12px]">No logs to display</p>
            <p className="text-[10px]">Logs appear here when your workspace is running</p>
          </div>
        ) : (
          filteredLogs.map((log) => <LogLine key={log.id} entry={log} />)
        )}
      </div>
    </div>
  );
}
